import { STATUS_META, PRIORITY_META } from "./ticketMeta";

/**
 * Timeline olaylarının görsel karşılıkları ve metin üretimi.
 *
 * Backend nötr veri gönderir (olay tipi + aktör + önceki/sonraki değer); başlık ve açıklama
 * burada üretilir. Sebebi: aynı olay bakan role göre farklı okunur — personel "Personel
 * yanıtladı" görürken talep sahibi "Destek tarafından yanıtlandı" görür.
 */

// Olay tipleri — backend'deki TicketEventType ile birebir aynı.
export const TIMELINE_EVENT = {
    CREATED: "CREATED",
    CUSTOMER_REPLIED: "CUSTOMER_REPLIED",
    STAFF_REPLIED: "STAFF_REPLIED",
    ATTACHMENT_ADDED: "ATTACHMENT_ADDED",
    ASSIGNED: "ASSIGNED",
    UNASSIGNED: "UNASSIGNED",
    STATUS_CHANGED: "STATUS_CHANGED",
    PRIORITY_CHANGED: "PRIORITY_CHANGED",
    CLOSED: "CLOSED",
    REOPENED: "REOPENED",
    AUTO_CLOSED: "AUTO_CLOSED",
};

const NAVY = "#221C5D";
const ORANGE = "#E8833A";
const GREEN = "#2BA66B";
const GRAY = "#8a93a6";
const SLATE = "#334155";

/** Sistem olaylarında (SLA otomatik kapanış) aktör yoktur. */
const SYSTEM_ACTOR = "Sistem";

export const TIMELINE_META = {
    [TIMELINE_EVENT.CREATED]: {
        icon: "bx-plus-circle", color: NAVY, bg: "#eaf0f8",
        title: () => "Destek talebi oluşturuldu",
    },
    [TIMELINE_EVENT.CUSTOMER_REPLIED]: {
        icon: "bx-message-rounded-dots", color: SLATE, bg: "#f1f5f9",
        title: (admin) => (admin ? "Müşteri yanıtladı" : "Mesajınız gönderildi"),
    },
    [TIMELINE_EVENT.STAFF_REPLIED]: {
        icon: "bx-support", color: NAVY, bg: "#eaf0f8",
        title: (admin) => (admin ? "Personel yanıtladı" : "Destek tarafından yanıtlandı"),
    },
    [TIMELINE_EVENT.ATTACHMENT_ADDED]: {
        icon: "bx-paperclip", color: SLATE, bg: "#f1f5f9",
        title: () => "Dosya eki eklendi",
    },
    [TIMELINE_EVENT.ASSIGNED]: {
        icon: "bx-user-check", color: GREEN, bg: "#e7f6ee",
        title: () => "Destek personeline atandı",
    },
    [TIMELINE_EVENT.UNASSIGNED]: {
        icon: "bx-user-x", color: GRAY, bg: "#f0f1f4",
        title: () => "Personel ataması kaldırıldı",
    },
    [TIMELINE_EVENT.STATUS_CHANGED]: {
        icon: "bx-transfer-alt", color: ORANGE, bg: "#fdf0e6",
        title: () => "Durum değiştirildi",
    },
    [TIMELINE_EVENT.PRIORITY_CHANGED]: {
        icon: "bx-flag", color: ORANGE, bg: "#fdf0e6",
        title: () => "Öncelik değiştirildi",
    },
    [TIMELINE_EVENT.CLOSED]: {
        icon: "bx-check-circle", color: GRAY, bg: "#f0f1f4",
        title: () => "Talep kapatıldı",
    },
    [TIMELINE_EVENT.REOPENED]: {
        icon: "bx-revision", color: NAVY, bg: "#eaf0f8",
        title: () => "Talep yeniden açıldı",
    },
    [TIMELINE_EVENT.AUTO_CLOSED]: {
        icon: "bx-time-five", color: GRAY, bg: "#f0f1f4",
        title: () => "Yanıt gelmediği için otomatik kapatıldı",
    },
};

const FALLBACK_META = { icon: "bx-circle", color: GRAY, bg: "#f0f1f4", title: () => "Hareket" };

/** Değeri okunur etikete çeviren sözlükler; listede olmayan tipler düz metin taşır (kullanıcı adı). */
const VALUE_LABELS = {
    [TIMELINE_EVENT.STATUS_CHANGED]: STATUS_META,
    [TIMELINE_EVENT.CLOSED]: STATUS_META,
    [TIMELINE_EVENT.REOPENED]: STATUS_META,
    [TIMELINE_EVENT.AUTO_CLOSED]: STATUS_META,
    [TIMELINE_EVENT.PRIORITY_CHANGED]: PRIORITY_META,
};

const labelFor = (type, value) => {
    if (!value) return null;
    return VALUE_LABELS[type]?.[value]?.label || value;
};

export const metaFor = (type) => TIMELINE_META[type] || FALLBACK_META;

export const titleFor = (entry, admin) => metaFor(entry.type).title(admin);

/** Olayın açıklaması: mesajlarda gövde özeti, değişimlerde "önceki → sonraki". */
export const descriptionFor = (entry) => {
    const { type, oldValue, newValue, attachmentCount } = entry;

    // Yazışma içeriği akışa taşınmaz; mesajlar zaten thread'de okunuyor.
    if (type === TIMELINE_EVENT.STAFF_REPLIED || type === TIMELINE_EVENT.CUSTOMER_REPLIED) {
        return null;
    }

    if (type === TIMELINE_EVENT.ATTACHMENT_ADDED) {
        return attachmentCount > 1 ? `${attachmentCount} dosya` : null;
    }
    if (type === TIMELINE_EVENT.ASSIGNED) {
        const previous = labelFor(type, oldValue);
        const current = labelFor(type, newValue);
        return previous ? `${previous} → ${current}` : current;
    }
    if (type === TIMELINE_EVENT.UNASSIGNED) {
        return labelFor(type, oldValue);
    }

    const previous = labelFor(type, oldValue);
    const current = labelFor(type, newValue);
    if (previous && current) return `${previous} → ${current}`;
    return current || previous || null;
};

/** Olayı yapan kişi; sistem olaylarında aktör yoktur. */
export const actorFor = (entry) => entry.actorUsername || SYSTEM_ACTOR;

/**
 * Adım çizginin üstünde mi dursun? Bakan tarafın kendi hareketleri üstte, karşı tarafınkiler
 * altta gösterilir — mesaj thread'indeki "kendi mesajın sağda" mantığının yatay karşılığı.
 * Sistem olayları (aktörü yok) destek tarafıyla birlikte altta kalır.
 */
export const isOwnSide = (entry, admin) =>
    admin ? entry.actorStaff === true : entry.actorStaff === false;

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat("tr", { numeric: "auto" });

/** Büyükten küçüğe: ilk eşleşen birim kullanılır. */
const RELATIVE_UNITS = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
];

/** "5 dakika önce", "dün", "2 ay önce" — bir dakikadan yeni olaylar için "az önce". */
export const fmtRelative = (value) => {
    if (!value) return "";
    const seconds = (new Date(value).getTime() - Date.now()) / 1000;
    const elapsed = Math.abs(seconds);
    if (elapsed < 60) return "az önce";
    const [unit, secondsInUnit] = RELATIVE_UNITS.find(([, size]) => elapsed >= size);
    return RELATIVE_FORMATTER.format(Math.round(seconds / secondsInUnit), unit);
};

/** Timeline satırlarında tarih ve saat ayrı gösterilir. */
export const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("tr-TR") : "-");

export const fmtTime = (value) =>
    value ? new Date(value).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "-";
